import React, { useEffect, useState } from 'react';
import api from '../api';
import { writePrintDoc } from '../utils/printUtils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Settings, IndianRupee, Calendar, Printer, FileText, Upload, X, Download, Eye, ArrowUp, ArrowDown } from 'lucide-react';
import { useAuth } from '../AuthContext';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import useAutoRefresh from '../hooks/useAutoRefresh';

const Funds = () => {
  const { isAdmin } = useAuth();
  const [funds, setFunds] = useState([]);
  const [fundHeads, setFundHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  
  // Fund Heads Management
  const [fundHeadDialogOpen, setFundHeadDialogOpen] = useState(false);
  const [fundHeadEditMode, setFundHeadEditMode] = useState(false);
  const [selectedFundHead, setSelectedFundHead] = useState(null);
  const [fundHeadFormData, setFundHeadFormData] = useState({ name: '', description: '' });
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('fund'); // 'fund' or 'fundHead'
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('all');

  const [formData, setFormData] = useState({
    fund_head: '',
    amount: 0,
    description: '',
    date: new Date().toISOString().split('T')[0],
    receipt_number: '',
  });

  // Sanction letter (file upload)
  const [letterFile, setLetterFile] = useState(null);
  const [letterDataUrl, setLetterDataUrl] = useState('');

  useEffect(() => {
    fetchFunds();
    fetchFundHeads();
    if (isAdmin()) {
      fetchBranches();
    }
  }, []);

  useEffect(() => {
    if (branches.length > 0) fetchFunds();
  }, [selectedBranchFilter]);

  // Real-time auto-refresh
  useAutoRefresh(() => fetchFunds());

  const [branches, setBranches] = useState([]);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
    } catch (error) {
      console.error('Failed to fetch branches');
    }
  };

  const fetchFunds = async () => {
    try {
      const params = {};
      if (isAdmin() && selectedBranchFilter !== 'all') {
        params.branch_id = selectedBranchFilter;
      }
      const response = await api.get('/fund-receipts', { params });
      setFunds(response.data);
    } catch (error) {
      toast.error('Failed to fetch funds');
    } finally {
      setLoading(false);
    }
  };

  const fetchFundHeads = async () => {
    try {
      const response = await api.get('/fund-heads');
      setFundHeads(response.data);
      // Set default fund head if not set
      if (response.data.length > 0 && !formData.fund_head) {
        setFormData(prev => ({ ...prev, fund_head: response.data[0].name }));
      }
    } catch (error) {
      console.error('Failed to fetch fund heads');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        date: new Date(formData.date).toISOString(),
      };
      if (letterFile && letterDataUrl) {
        payload.sanction_letter = letterDataUrl;
        payload.sanction_letter_filename = letterFile.name;
        payload.sanction_letter_mime_type = letterFile.type || 'application/octet-stream';
      }

      if (editMode && selectedFund) {
        await api.put(`/fund-receipts/${selectedFund.id}`, payload);
        toast.success('Fund receipt updated successfully');
      } else {
        await api.post('/fund-receipts', payload);
        toast.success('Fund receipt recorded successfully');
      }
      closeDialog();
      fetchFunds();
    } catch (error) {
      toast.error('Failed to save fund receipt');
    }
  };

  const handleLetterFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Sanction letter file too large (max 10 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLetterFile(f);
      setLetterDataUrl(reader.result);
    };
    reader.readAsDataURL(f);
  };

  const downloadSanctionLetter = async (fund) => {
    try {
      const r = await api.get(`/fund-receipts/${fund.id}/sanction-letter`);
      const a = document.createElement('a');
      a.href = r.data.sanction_letter;
      a.download = r.data.sanction_letter_filename || `sanction_${fund.receipt_number || fund.id}`;
      a.click();
    } catch {
      toast.error('Failed to download sanction letter');
    }
  };

  const viewSanctionLetter = async (fund) => {
    try {
      const r = await api.get(`/fund-receipts/${fund.id}/sanction-letter`);
      const w = window.open();
      if (w) {
        const isPdf = r.data.sanction_letter_mime_type === 'application/pdf';
        const isImage = r.data.sanction_letter_mime_type?.startsWith('image/');
        if (isPdf) {
          writePrintDoc(w.document, `<html><head><title>Sanction Letter</title></head><body style="margin:0"><iframe src="${r.data.sanction_letter}" style="width:100%;height:100vh;border:0"></iframe></body></html>`);
        } else if (isImage) {
          writePrintDoc(w.document, `<html><head><title>Sanction Letter</title></head><body><img src="${r.data.sanction_letter}" style="max-width:100%"/></body></html>`);
        } else {
          w.location = r.data.sanction_letter;
        }
      }
    } catch {
      toast.error('Failed to open sanction letter');
    }
  };

  const handleEdit = (fund) => {
    setSelectedFund(fund);
    setFormData({
      fund_head: fund.fund_head,
      amount: fund.amount,
      description: fund.description,
      date: new Date(fund.date).toISOString().split('T')[0],
      receipt_number: fund.receipt_number,
    });
    setLetterFile(null);
    setLetterDataUrl('');
    setEditMode(true);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedFund(null);
    setFormData({
      fund_head: fundHeads.length > 0 ? fundHeads[0].name : '',
      amount: 0,
      description: '',
      date: new Date().toISOString().split('T')[0],
      receipt_number: '',
    });
    setLetterFile(null);
    setLetterDataUrl('');
  };

  // Fund Head Management Functions
  const handleFundHeadSubmit = async (e) => {
    e.preventDefault();
    try {
      if (fundHeadEditMode && selectedFundHead) {
        await api.put(`/fund-heads/${selectedFundHead.id}`, fundHeadFormData);
        toast.success('Fund head updated successfully');
      } else {
        await api.post('/fund-heads', fundHeadFormData);
        toast.success('Fund head created successfully');
      }
      closeFundHeadDialog();
      fetchFundHeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save fund head');
    }
  };

  const handleEditFundHead = (fundHead) => {
    setSelectedFundHead(fundHead);
    setFundHeadFormData({
      name: fundHead.name,
      description: fundHead.description || '',
    });
    setFundHeadEditMode(true);
    setFundHeadDialogOpen(true);
  };

  const closeFundHeadDialog = () => {
    setFundHeadDialogOpen(false);
    setFundHeadEditMode(false);
    setSelectedFundHead(null);
    setFundHeadFormData({ name: '', description: '' });
  };

  // Move fund head up/down (reorder)
  const moveFundHead = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= fundHeads.length) return;
    const reordered = [...fundHeads];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setFundHeads(reordered); // optimistic
    try {
      await api.post('/fund-heads/reorder', { ids: reordered.map(h => h.id) });
      toast.success('Order updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reorder');
      fetchFundHeads(); // rollback
    }
  };

  // Delete fund head with smart force-prompt when in use
  const handleDeleteFundHead = async (head) => {
    const baseConfirm = `Delete fund head "${head.name}"?${head.is_default ? ' (This is a DEFAULT fund head — it will reappear on next system boot if not seeded otherwise.)' : ''}`;
    if (!window.confirm(baseConfirm)) return;
    try {
      await api.delete(`/fund-heads/${head.id}`);
      toast.success(`Fund head "${head.name}" deleted`);
      fetchFundHeads();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      // Backend tells us how many receipts use it; offer force-delete
      if (detail.includes('used in') && err.response?.status === 400) {
        if (window.confirm(`${detail}\n\nForce delete anyway? Existing receipts will keep "${head.name}" stored as plain text.`)) {
          try {
            await api.delete(`/fund-heads/${head.id}?force=true`);
            toast.success(`Fund head "${head.name}" force-deleted`);
            fetchFundHeads();
          } catch (err2) {
            toast.error(err2.response?.data?.detail || 'Failed to force delete');
          }
        }
      } else {
        toast.error(detail || 'Failed to delete fund head');
      }
    }
  };

  // Delete Functions
  const openDeleteDialog = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      if (deleteType === 'fund') {
        await api.delete(`/fund-receipts/${itemToDelete.id}`);
        toast.success('Fund receipt deleted successfully');
        fetchFunds();
      } else {
        await api.delete(`/fund-heads/${itemToDelete.id}`);
        toast.success('Fund head deleted successfully');
        fetchFundHeads();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  // Group funds by fund head
  const groupedFunds = funds.reduce((acc, fund) => {
    const head = fund.fund_head;
    if (!acc[head]) {
      acc[head] = { funds: [], total: 0 };
    }
    acc[head].funds.push(fund);
    acc[head].total += fund.amount;
    return acc;
  }, {});

  const totalFunds = funds.reduce((sum, f) => sum + f.amount, 0);

  return (
    <div className="p-4 md:p-6" data-testid="funds-page">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Funds Management</h1>
          <p className="text-sm text-slate-600">Track fund receipts and manage fund heads</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {isAdmin() && (
            <Select value={selectedBranchFilter} onValueChange={setSelectedBranchFilter}>
              <SelectTrigger className="w-48" data-testid="branch-filter-funds">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {isAdmin() && (
            <Button
              variant="outline"
              onClick={() => setFundHeadDialogOpen(true)}
              data-testid="manage-fund-heads-button"
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            >
              <Settings className="w-4 h-4 mr-2" />
              Manage Fund Heads
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-fund-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Fund Receipt
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editMode ? 'Edit Fund Receipt' : 'Add Fund Receipt'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Fund Head *</Label>
                  <Select
                    value={formData.fund_head}
                    onValueChange={(value) => setFormData({ ...formData, fund_head: value })}
                  >
                    <SelectTrigger data-testid="fund-head-select">
                      <SelectValue placeholder="Select Fund Head" />
                    </SelectTrigger>
                    <SelectContent>
                      {fundHeads.map((head) => (
                        <SelectItem key={head.id} value={head.name}>{head.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Amount (₹) *</Label>
                  <Input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                    required
                    data-testid="fund-amount-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sanction No.</Label>
                  <Input
                    value={formData.receipt_number}
                    onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })}
                    placeholder="e.g. SANC-2026/045"
                    data-testid="fund-receipt-number-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sanction Letter (PDF / Image, max 10 MB)</Label>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-md text-xs font-medium text-amber-700 hover:bg-amber-100 transition" data-testid="fund-sanction-upload-label">
                      <Upload className="w-3.5 h-3.5" />
                      {letterFile ? 'Change File' : (selectedFund?.has_sanction_letter ? 'Replace Letter' : 'Upload Sanction Letter')}
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleLetterFileSelect} className="hidden" data-testid="fund-sanction-file-input" />
                    </label>
                    {letterFile && (
                      <div className="text-xs text-emerald-700 flex items-center gap-1">
                        <FileText className="w-3 h-3" /> {letterFile.name} ({(letterFile.size / 1024).toFixed(1)} KB)
                        <button type="button" onClick={() => { setLetterFile(null); setLetterDataUrl(''); }} className="ml-1 text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>
                      </div>
                    )}
                    {!letterFile && selectedFund?.has_sanction_letter && (
                      <span className="text-xs text-slate-500 italic">A sanction letter is on file. Choose a new file to replace it.</span>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="fund-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Add notes..."
                    data-testid="fund-description-input"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
                  <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-fund-button">
                    {editMode ? 'Update' : 'Save'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Fund Heads Management Dialog */}
      <Dialog open={fundHeadDialogOpen} onOpenChange={(open) => { if (!open) closeFundHeadDialog(); else setFundHeadDialogOpen(true); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Fund Heads</DialogTitle>
          </DialogHeader>
          
          {/* Add New Fund Head Form */}
          <form onSubmit={handleFundHeadSubmit} className="p-4 bg-indigo-50 rounded-lg mb-4">
            <h4 className="font-medium text-indigo-900 mb-3">
              {fundHeadEditMode ? 'Edit Fund Head' : 'Add New Fund Head'}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={fundHeadFormData.name}
                  onChange={(e) => setFundHeadFormData({ ...fundHeadFormData, name: e.target.value })}
                  placeholder="Fund head name"
                  required
                  data-testid="fund-head-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={fundHeadFormData.description}
                  onChange={(e) => setFundHeadFormData({ ...fundHeadFormData, description: e.target.value })}
                  placeholder="Optional description"
                  data-testid="fund-head-description-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              {fundHeadEditMode && (
                <Button type="button" variant="outline" onClick={closeFundHeadDialog}>Cancel</Button>
              )}
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-fund-head-button">
                {fundHeadEditMode ? 'Update' : 'Add Fund Head'}
              </Button>
            </div>
          </form>

          {/* Fund Heads List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-slate-900">Existing Fund Heads</h4>
              {isAdmin() && <p className="text-xs text-slate-500">Use ↑ ↓ to reorder. Reordering applies instantly.</p>}
            </div>
            <div className="border rounded-lg divide-y">
              {fundHeads.map((head, idx) => (
                <div key={head.id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                  <div className="flex items-center gap-3 flex-1">
                    {isAdmin() && (
                      <div className="flex flex-col gap-0.5">
                        <button
                          type="button"
                          onClick={() => moveFundHead(idx, idx - 1)}
                          disabled={idx === 0}
                          className="p-0.5 text-slate-500 hover:text-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                          data-testid={`move-up-${head.id}`}
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveFundHead(idx, idx + 1)}
                          disabled={idx === fundHeads.length - 1}
                          className="p-0.5 text-slate-500 hover:text-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                          data-testid={`move-down-${head.id}`}
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{head.name}</p>
                      {head.description && <p className="text-xs text-slate-500">{head.description}</p>}
                      {head.is_default && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 mr-1">Default</span>
                      )}
                      <span className="text-[10px] text-slate-400">order: {head.display_order ?? idx}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditFundHead(head)}
                      className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                      data-testid={`edit-fund-head-${head.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {isAdmin() && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteFundHead(head)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        data-testid={`delete-fund-head-${head.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <IndianRupee className="w-4 h-4 text-emerald-600" />
            <span className="text-xs text-emerald-700 font-medium">Total Funds</span>
          </div>
          <p className="text-xl font-bold text-emerald-900">₹{totalFunds.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-700 font-medium">Total Receipts</span>
          </div>
          <p className="text-xl font-bold text-blue-900">{funds.length}</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Settings className="w-4 h-4 text-purple-600" />
            <span className="text-xs text-purple-700 font-medium">Fund Heads</span>
          </div>
          <p className="text-xl font-bold text-purple-900">{fundHeads.length}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <IndianRupee className="w-4 h-4 text-amber-600" />
            <span className="text-xs text-amber-700 font-medium">Active Heads</span>
          </div>
          <p className="text-xl font-bold text-amber-900">{Object.keys(groupedFunds).length}</p>
        </div>
      </div>

      {/* Funds Table by Head */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Print Button for Admin */}
          {isAdmin() && funds.length > 0 && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const htmlContent = `
                    <html>
                      <head>
                        <title>Fund Receipts Report - WWAGS MIS</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 20px; }
                          h1 { color: #047857; font-size: 20px; margin-bottom: 5px; }
                          p { color: #666; font-size: 12px; margin-bottom: 15px; }
                          table { width: 100%; border-collapse: collapse; font-size: 11px; }
                          th { background: #047857; color: white; padding: 8px; text-align: left; }
                          td { padding: 6px 8px; border-bottom: 1px solid #ddd; }
                          tr:nth-child(even) { background: #f9f9f9; }
                          .amount { text-align: right; font-weight: bold; }
                          .total { background: #d1fae5; font-weight: bold; }
                          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
                        </style>
                      </head>
                      <body>
                        <h1>WWAGS MIS - Fund Receipts Report</h1>
                        <p>Generated on: ${new Date().toLocaleString('en-IN')} | Total Receipts: ${funds.length} | Total Amount: Rs.${totalFunds.toLocaleString('en-IN')}</p>
                        <table>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Branch</th>
                              <th>Fund Head</th>
                              <th>Sanction No.</th>
                              <th class="amount">Amount</th>
                              <th>Description</th>
                              <th>Letter?</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${funds.map(fund => `
                              <tr>
                                <td>${new Date(fund.date).toLocaleDateString('en-IN')}</td>
                                <td>${branches.find(b => b.id === fund.branch_id)?.code || '-'}</td>
                                <td>${fund.fund_head}</td>
                                <td>${fund.receipt_number || '-'}</td>
                                <td class="amount">Rs.${fund.amount.toLocaleString('en-IN')}</td>
                                <td>${fund.description || '-'}</td>
                                <td>${fund.has_sanction_letter ? 'Yes' : '-'}</td>
                              </tr>
                            `).join('')}
                            <tr class="total">
                              <td colspan="4" style="text-align: right;">Grand Total:</td>
                              <td class="amount">Rs.${totalFunds.toLocaleString('en-IN')}</td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </body>
                    </html>
                  `;
                  const printWindow = window.open('', '_blank');
                  if (!printWindow) {
                    const iframe = document.createElement('iframe');
                    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
                    document.body.appendChild(iframe);
                    writePrintDoc(iframe.contentDocument, htmlContent);
                    setTimeout(() => { iframe.contentWindow.print(); setTimeout(() => document.body.removeChild(iframe), 1000); }, 500);
                    return;
                  }
                  writePrintDoc(printWindow.document, htmlContent);
                  setTimeout(() => printWindow.print(), 500);
                }}
                data-testid="print-funds-button"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Report
              </Button>
            </div>
          )}
          
          {Object.entries(groupedFunds).map(([head, data]) => (
            <div key={head} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-4 py-3 flex justify-between items-center border-b">
                <h3 className="font-semibold text-slate-900">{head}</h3>
                <span className="text-sm font-medium text-emerald-700">
                  Total: ₹{data.total.toLocaleString('en-IN')}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                      {isAdmin() && <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Branch</th>}
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Sanction No.</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Description</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-slate-600">Letter</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.funds.map((fund) => (
                      <tr key={fund.id} className="hover:bg-slate-50">
                        <td className="px-4 py-2">{new Date(fund.date).toLocaleDateString('en-IN')}</td>
                        {isAdmin() && (
                          <td className="px-4 py-2 text-xs">
                            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                              {branches.find(b => b.id === fund.branch_id)?.code || '-'}
                            </span>
                          </td>
                        )}
                        <td className="px-4 py-2 font-mono text-xs">{fund.receipt_number || '-'}</td>
                        <td className="px-4 py-2 text-right font-medium text-emerald-700">
                          ₹{fund.amount.toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-2 text-slate-600">{fund.description || '-'}</td>
                        <td className="px-4 py-2 text-center">
                          {fund.has_sanction_letter ? (
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => viewSanctionLetter(fund)} className="text-blue-700 hover:text-blue-900" title="View letter" data-testid={`view-letter-${fund.id}`}>
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => downloadSanctionLetter(fund)} className="text-emerald-700 hover:text-emerald-900" title="Download letter" data-testid={`download-letter-${fund.id}`}>
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(fund)}
                              className="h-7 w-7 p-0 text-blue-600"
                              data-testid={`edit-fund-${fund.id}`}
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            {isAdmin() && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openDeleteDialog(fund, 'fund')}
                                className="h-7 w-7 p-0 text-red-600"
                                data-testid={`delete-fund-${fund.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {funds.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <IndianRupee className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No fund receipts recorded yet</p>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={deleteType === 'fund' ? 'Delete Fund Receipt' : 'Delete Fund Head'}
        message={deleteType === 'fund' 
          ? `Are you sure you want to delete this fund receipt of ₹${itemToDelete?.amount?.toLocaleString('en-IN')}?`
          : `Are you sure you want to delete fund head "${itemToDelete?.name}"?`
        }
      />
    </div>
  );
};

export default Funds;

